/**
 * Detalle de una jornada, en un modal: los tres insumos del cruce, lado a
 * lado (§11).
 *
 * Que estén los tres a la vista es el punto: el supervisor tiene que poder
 * ver de dónde salió el estado sin abrir tres pantallas. Un insumo ausente se
 * dice con palabras ("No está en ningún viaje"), no con un guion.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import type { DecisionSupervisor, JornadaDto } from '../../api';
import { Modal } from '../../components/Modal';
import { useSesion } from '../../components/useSesion';
import { aDdMmAaaa, fechaHoraLegible } from '../../lib/fechas';
import { A_RESOLVER, grupoDe } from './dominio';

type FilaPanel = readonly [etiqueta: string, valor: string | null, ausente: string];

export interface PropsDetalle {
  readonly jornada: JornadaDto;
  readonly resolviendo: boolean;
  readonly onCerrar: () => void;
  readonly onResolver: (decision: DecisionSupervisor, comentario: string) => void;
  /** Procesar desde el detalle: pide la misma confirmación que la fila. */
  readonly onProcesar: (j: JornadaDto) => void;
}

export function DetalleJornada({
  jornada: j,
  resolviendo,
  onCerrar,
  onResolver,
  onProcesar,
}: PropsDetalle): React.JSX.Element {
  // Las marcas de tiempo se muestran en la zona operativa del portal.
  const { modulo } = useSesion();
  const zona = modulo?.zona;

  /*
   * Encabezado en dos líneas: arriba la persona, abajo su identidad. La
   * empresa y el grupo van acá y no en un panel: son identidad de la persona,
   * no un insumo del cruce.
   */
  const titulo = j.nombre ?? `Dni ${j.dni}`;
  const identidad =
    `Dni ${j.dni} · ${aDdMmAaaa(j.fechaOperativa)}` +
    (j.empresa ? ` · ${j.empresa.toUpperCase()}` : '') +
    (j.grupo ? ` · ${j.grupo}` : '');

  return (
    <Modal
      abierto
      titulo={titulo}
      identidad={identidad}
      insignia={
        <span className="pastilla" data-estado={j.estado}>
          {j.etiquetaEstado}
        </span>
      }
      onCerrar={onCerrar}
      ancho="amplio"
      /*
       * La acción también vive en el pie del detalle: quien abrió la jornada
       * para entenderla tenía que cerrar el modal y volver a buscar la fila
       * para poder procesarla.
       */
      {...(j.admiteImpacto
        ? {
            pie: (
              <>
                <button type="button" className="plano" onClick={onCerrar}>
                  Cerrar
                </button>
                <button type="button" onClick={() => onProcesar(j)}>
                  Procesar la jornada
                </button>
              </>
            ),
          }
        : {})}
    >
      <div className="paneles">
        <Panel
          titulo="Fichada — GeoVictoria"
          filas={[
            ['Entrada', j.fichadaEntrada?.hora ?? null, 'No registró fichada de entrada'],
            ['Origen', j.fichadaEntrada?.origen ?? null, '—'],
            ['Salida real', j.fichadaSalida?.hora ?? null, 'Sin salida registrada'],
          ]}
        />
        <Panel
          titulo="Viaje — TMS"
          filas={[
            ['Viaje', j.viaje?.identificadorViaje ?? null, 'No está en ningún viaje'],
            ['Inicio', j.viaje?.inicio ? aDdMmAaaa(j.viaje.inicio) : null, '—'],
            ['Fin', j.viaje?.fin ? aDdMmAaaa(j.viaje.fin) : null, '—'],
            [
              'Función',
              j.viaje ? (j.viaje.funcion === 'ACOMPANANTE' ? 'Acompañante' : 'Chofer') : null,
              '—',
            ],
            ['Detalle', j.viaje?.descripcion ?? null, '—'],
          ]}
        />
        <Panel
          titulo="Turno configurado"
          filas={[
            [
              'Turno',
              j.turno ? `${j.turno.horaInicio}–${j.turno.horaFin}` : null,
              'Sin turno configurado',
            ],
            ['Cruza medianoche', j.turno ? (j.turno.cruzaMedianoche ? 'Sí' : 'No') : null, '—'],
            ['Origen', j.turno?.origen ?? null, '—'],
            [
              'Salida a marcar',
              // Con fichada de salida real no se marca nada (§12.4).
              j.fichadaSalida
                ? 'No corresponde: ya fichó salida'
                : j.horaSalidaCalculada && j.fechaSalidaCalculada
                ? `${aDdMmAaaa(j.fechaSalidaCalculada)} ${j.horaSalidaCalculada}` +
                  (j.fechaSalidaCalculada !== j.fechaOperativa ? ' (día siguiente)' : '')
                : null,
              'No se puede calcular',
            ],
          ]}
        />
      </div>

      <div className="detalle-estado" data-estado={j.estado} data-grupo={grupoDe(j.estado)}>
        <strong>{j.etiquetaEstado}</strong>
        {j.motivo}
        {j.etiquetasAcciones.length > 0 && (
          <>
            <p className="nota-acciones">Se va a registrar:</p>
            {/* Numeradas en el orden real: primero la licencia, después la
                salida al fin del turno. */}
            <ol className="detalle-acciones">
              {j.etiquetasAcciones.map((accion, i) => (
                <li key={accion}>
                  <span className="orden">{String(i + 1)}</span>
                  {accion}
                </li>
              ))}
            </ol>
          </>
        )}
      </div>

      <Impactos jornada={j} zona={zona} />

      {A_RESOLVER.includes(j.estado) && (
        <FormResolver resolviendo={resolviendo} onResolver={onResolver} />
      )}
    </Modal>
  );
}

function Panel({
  titulo,
  filas,
}: {
  readonly titulo: string;
  readonly filas: readonly FilaPanel[];
}): React.JSX.Element {
  return (
    <article className="panel">
      <h3>{titulo}</h3>
      <dl>
        {filas.map(([etiqueta, valor, ausente]) => (
          <div key={etiqueta} className="panel-fila">
            <dt>{etiqueta}</dt>
            <dd className={valor ? undefined : 'ausente'}>{valor || ausente}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

function Impactos({
  jornada: j,
  zona,
}: {
  readonly jornada: JornadaDto;
  readonly zona: string | undefined;
}): React.JSX.Element | null {
  if (j.impactos.length === 0 && !j.resolucion) return null;

  return (
    <div className="impactos">
      {j.impactos.length > 0 && (
        <>
          <h3>Impactos previos</h3>
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Tipo</th>
                  <th scope="col">Resultado</th>
                  <th scope="col">Usuario</th>
                  <th scope="col" className="num">Fecha y hora</th>
                  <th scope="col">Referencia</th>
                </tr>
              </thead>
              <tbody>
                {j.impactos.map((i) => (
                  <tr key={i.id}>
                    <td>{i.tipoImpacto}</td>
                    <td>{i.resultado}</td>
                    <td>{i.usuario}</td>
                    <td className="num">{fechaHoraLegible(i.fechaHora, zona)}</td>
                    <td className="truncado">{i.referenceIdentifier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <details>
            <summary>Ver payloads enviados y respuestas de GeoVictoria</summary>
            <pre>
              {JSON.stringify(
                j.impactos.map((i) => ({
                  tipo: i.tipoImpacto,
                  payloadEnviado: i.payloadEnviado,
                  respuestaGv: i.respuestaGv,
                  detalle: i.detalle,
                })),
                null,
                2,
              )}
            </pre>
          </details>
        </>
      )}

      {j.resolucion && (
        <p className="nota">
          {`Resuelta por ${j.resolucion.usuario} el ${fechaHoraLegible(j.resolucion.fechaHora, zona)}: ` +
            `${j.resolucion.decision} — ${j.resolucion.comentario}`}
        </p>
      )}
    </div>
  );
}

function FormResolver({
  resolviendo,
  onResolver,
}: {
  readonly resolviendo: boolean;
  readonly onResolver: (decision: DecisionSupervisor, comentario: string) => void;
}): React.JSX.Element {
  const [decision, setDecision] = useState<DecisionSupervisor>('CONFIRMA_ASISTENCIA');
  const [comentario, setComentario] = useState('');

  return (
    <form
      className="resolver"
      onSubmit={(ev) => {
        ev.preventDefault();
        onResolver(decision, comentario);
        setComentario('');
      }}
    >
      <h3>Resolver la inconsistencia</h3>
      <div className="banda">
        <label className="campo">
          Decisión
          <select
            value={decision}
            onChange={(ev) => setDecision(ev.target.value as DecisionSupervisor)}
          >
            <option value="CONFIRMA_ASISTENCIA">Confirma la asistencia</option>
            <option value="DESCARTA">Descarta la jornada</option>
            <option value="DERIVA">Deriva el caso</option>
          </select>
        </label>
        <label className="campo campo--crece">
          Comentario (obligatorio)
          <input
            type="text"
            minLength={10}
            maxLength={1000}
            required
            placeholder="Qué se verificó y con quién"
            value={comentario}
            onChange={(ev) => setComentario(ev.target.value)}
          />
        </label>
        <button type="submit" disabled={resolviendo}>
          {resolviendo && <Loader2 className="girando" size={14} aria-hidden="true" />}
          {resolviendo ? 'Resolviendo…' : 'Resolver'}
        </button>
      </div>
    </form>
  );
}
